/* eslint-disable react/jsx-no-bind */
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Button,
  Typography,
  Select,
  Tooltip,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { isMobile } from 'react-device-detect';
import { actions, selectors, hooks } from 'farmx-redux-core';
import { recommendationApi } from 'farmx-api';
import RecommendationLoader from '../../contentLoaders/RecommendationContentLoader';
import { PageHeader } from '../components/PageHeader';
import { CardLayout } from '../components/CardLayout';
import { getActionText } from './recommendation';
import { useTracking } from '../../../helper/mixpanel';
import { PercentAvailableWaterGraph } from './PercentAvailableWaterGraph';
import { EtcDeficitGraph } from './EtcDeficitGraph';
import './recommendation-detail.css';
import RanchBlockTitle from '../components/RanchBlockTitle';
import NewSensorDataChart from '../../graph/NewSensorDataChart';
import {
  recommendationAnomalyFilter,
  checkIsAllRanch,
  checkIsActionNeeded,
  parseWater,
} from '../../../helper/common';
import {
  formatIrrigationDepthUnit,
  formatIrrigationDepthValue,
  formatCropName,
} from './recommendationFormat';
import {
  formatRecommendationDuration,
  getActionableDurationValue,
} from '../../../helper/recommendationDisplayHelper';
import { useNavigateHome } from '../../../helper/recommendationHooks';
import { IrrigationEquationDetails } from '../components/IrrigationEquationDetails';
import { RecommendationGraphs } from './RecommendationGraphs';
import ForecastCard from './ForecastCard';
import {
  IRRIGATION_RECOMMENDATION_STATES,
  RECOMMENDATION_PRIORITY_LEVELS,
} from '../../../constants';
import { RecommendationRefreshStatusMessage } from './RecommendationRefreshStatusMessage';
import { useRecommendationRefreshStatus } from './useRecommendationRefreshStatus';

const REFRESH_COOLDOWN_SECONDS = 15 * 60;

const {
  selectRecommendationForBlock,
  selectNewBlockById,
  selectSensorsByRanchId,
  selectLoadingRecommendations,
  selectUserLengthFormat,
} = selectors;

const {
  loadRecommendationForBlock,
  updateRecommendation,
  setBlocks,
  loadRecommendations,
} = actions;

const { useRanchBlockSelection, useEntities } = hooks;

export function RecommendationDetail(props) {
  const {
    showMap, onClick, propBlockId, showGraphButtons,
  } = props;
  const { t } = useTranslation();
  const getUserUnits = hooks.useUnits();
  const tracking = useTracking();
  const { identifier } = useParams();
  const blkId = parseInt(identifier, 10);
  const blockId = blkId || propBlockId;
  const dispatch = useDispatch();
  useRecommendationRefreshStatus(blockId);
  const location = useLocation();
  const history = useHistory();
  const [infoType, setInfoType] = useState(!showMap ? 'deficit' : 'vwc');
  const [days, setDays] = useState(10);
  const [tableVisible, setTableVisibile] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshCooldownSeconds, setRefreshCooldownSeconds] = useState(0);
  const refreshCooldownTimer = useRef(null);
  const { useBlockNames, useRanchNamesForBlocks } = hooks;
  const blockName = useBlockNames([Number(blockId)]);
  const ranchName = useRanchNamesForBlocks([Number(blockId)]);
  const { selectedTab } = location.state ?? {};
  const recommendation = useSelector((state) => recommendationAnomalyFilter(
    selectRecommendationForBlock(state, blockId),
  ));
  const loadingStatus = useSelector((state) => selectLoadingRecommendations(state));
  const units = recommendation?.units || {
    deficit: 'millimeters',
    etcForecast: 'millimeters',
    totalWater: 'millimeters',
  };
  const { selectedObjFromState } = useRanchBlockSelection();

  const { activeEntityId } = useEntities();

  useNavigateHome();

  const irrigationModel = recommendation?.irrigationModel || 'soil_water_deficit';
  const block = useSelector((state) => selectNewBlockById(state, Number(blockId))) || {};
  const sensors = useSelector((state) => selectSensorsByRanchId(state, block.ranch))
    .filter((d) => d?.block === block.id && (d?.type === 'aquacheck_soil'
    || d?.type === 'pixl_soil'));
  const isAllRanch = checkIsAllRanch(selectedObjFromState?.type);
  const sensor = sensors.length ? sensors[0] : { type: null, identifier: null, id: null };
  const userUnitLength = useSelector((state) => selectUserLengthFormat(state));
  const toUnitByLength = userUnitLength === 'feet' ? 'inches' : 'millimeters';
  const { custom_crop_coefficient: customKcValue } = block || {};
  const cropName = block?.crop;
  const dueDate = recommendation?.data?.recommendedIrrigationStart;
  const staleReason = recommendation?.data?.staleReason;
  const isStale = Boolean(staleReason);

  const rootZoneVWCChart = useMemo(() => (
    <NewSensorDataChart
      sensorId={sensor.id}
      sensorType={sensor.type}
      sensorIdentifier={sensor.identifier}
      variables={['soil_moisture_rootzone_vwc']}
      startDate={moment().subtract(days, 'days')}
      endDate={moment()}
      header={false}
    />
  ), [sensor.id, sensor.identifier, sensor.type, days]);

  useEffect(() => {
    if (blockId) {
      tracking.track('Loaded Recommendation Detail Page', { blockId });
    }
  }, [tracking, blockId]);

  useEffect(() => {
    if (blockId) {
      dispatch(loadRecommendationForBlock(blockId));
      if(blkId){
        dispatch(setBlocks([blockId]));
      }
    }

    return () => {
      if(selectedObjFromState.type === 'ranch') {
        dispatch(setBlocks([block?.ranch]));
      }
      if(isAllRanch) {
        dispatch(setBlocks([]));
      }
    };
  }, [dispatch, blockId]);

  useEffect(() => {
    if (!recommendation?.id && activeEntityId) {
      dispatch(loadRecommendations(activeEntityId));
    }
  }, [dispatch, recommendation, activeEntityId]);

  useEffect(() => {
    if (refreshCooldownSeconds <= 0) {
      if (refreshCooldownTimer.current) {
        clearInterval(refreshCooldownTimer.current);
        refreshCooldownTimer.current = null;
      }
      return undefined;
    }

    if (!refreshCooldownTimer.current) {
      refreshCooldownTimer.current = setInterval(() => {
        setRefreshCooldownSeconds((seconds) => Math.max(seconds - 1, 0));
      }, 1000);
    }

    return () => {
      if (refreshCooldownTimer.current) {
        clearInterval(refreshCooldownTimer.current);
        refreshCooldownTimer.current = null;
      }
    };
  }, [refreshCooldownSeconds]);

  if (!recommendation && !loadingStatus.loading) {
    return (
      <div>Recommendation not found</div>
    );
  }

  if (loadingStatus.loading && !recommendation) {
    return (
      <RecommendationLoader />
    );
  }

  const dendrometerCount = 0;
  const duration = recommendation ? moment.duration(recommendation.duration).asDays() : null;
  const totalWaterValue = recommendation?.totalWater !== null
    && recommendation?.totalWater !== undefined
    ? parseWater(
      recommendation.totalWater,
      units.totalWater,
      getUserUnits,
      toUnitByLength,
      t,
    )
    : null;
  const irrigationDurationSource = recommendation?.data?.recommendedIrrigationDuration;
  const irrigationDurationValue = getActionableDurationValue(irrigationDurationSource);
  const depthUnit = getUserUnits(
    0,
    'inches',
    undefined,
    { decimalPlaces: 2 },
    toUnitByLength,
  )?.label || 'inches';
  const deficitValue = recommendation?.deficit !== null
    && recommendation?.deficit !== undefined
    ? parseWater(
      recommendation.deficit,
      units.deficit,
      getUserUnits,
      toUnitByLength,
      t,
    )
    : null;
  const etcImpactValue = recommendation?.etcForecast !== null
    && recommendation?.etcForecast !== undefined
    ? parseWater(
      recommendation.etcForecast,
      units.etcForecast,
      getUserUnits,
      toUnitByLength,
      t,
    )
    : null;

  function getSoilMessage() {
    if (recommendation.deficit === null || recommendation.deficit === undefined) {
      return null;
    }
    if (recommendation?.state === 'well_irrigated' && recommendation?.action === 'irrigate') {
      return t('Current soil moisture is healthy, but forecast demand is expected to increase.');
    }
    if (recommendation.deficit >= 0) {
      return t('Soil moisture is below the refill point on average.');
    }
    return t('Soil moisture is above the refill point on average.');
  }

  function getEtcDeficitMessage() {
    if (recommendation.deficit === null || recommendation.deficit === undefined) {
      return null;
    }
    if (recommendation.deficit >= 0) {
      return t('Applied irrigation is running below measured ETc this season.');
    }
    return t('Applied irrigation is running above measured ETc this season.');
  }

  function getDeficitMessage() {
    if (irrigationModel === 'soil_water_deficit') {
      return getSoilMessage();
    }
    if (irrigationModel === 'etc_deficit') {
      return getEtcDeficitMessage();
    }
    return null;
  }

  function getEtcMessage() {
    if (recommendation.etcForecast === null || recommendation.etcForecast === undefined) {
      return null;
    }
    if (recommendation.deficit < 0) {
      return t('Forecast ETc is expected to reduce the current surplus over the next {{days}} days.', {
        days: duration,
      });
    }
    return t('Forecast ETc is expected to increase the water need over the next {{days}} days.', {
      days: duration,
    });
  }

  function getIrrigationDescriptionLong() {
    const messages = [];
    const deficitMessage = getDeficitMessage();
    const etcMessage = getEtcMessage();
    const dendrometerMessage = `${t(recommendation.cropStress)} ${t('is indicated across')}
     ${dendrometerCount} ${t('dendrometers')}`;

    if (deficitMessage) {
      messages.push(deficitMessage);
    }
    if (etcMessage) {
      messages.push(etcMessage);
    }
    if (dendrometerCount && recommendation.cropStress) {
      messages.push(dendrometerMessage);
    }
    return messages.join(' ');
  }

  function renderGraphButtons() {
    return (
      <div style={{ width: '50%' }}>
        {!showMap && (
        <Button
          style={{ width: '60px', padding: '0' }}
          type={infoType === 'deficit' ? 'primary' : 'default'}
          onClick={() => {
            if (infoType !== 'deficit') {
              setInfoType('deficit');
            }
          }}
        >
          {t('Deficit')}
        </Button>
        )}

        {showMap && (
        <Button
          style={{ width: '60px', padding: '0' }}
          type="default"
          onClick={() => {
            if (onClick) onClick('map');
          }}
        >
          {t('Map')}
        </Button>
        )}

        <Button
          style={{ width: '60px', padding: '0' }}
          type={(infoType === 'vwc' || showMap) ? 'primary' : 'default'}
          onClick={() => {
            if (!showMap && infoType !== 'vwc') {
              setInfoType('vwc');
            }
            if (showMap && onClick) onClick('vwc');
          }}
        >
          {t('VWC')}
        </Button>
      </div>
    );
  }

  function renderSoilGraphs() {
    return (
      <div className="recommendation-detail-card2-container">
        <div className="recommendation-detail-card2-top">
          {showGraphButtons && !showMap ? renderGraphButtons() : (
            <div style={{ width: '50%' }}>
              <div className="recommendation-detail-card2-deficit">
                <Typography.Text>{t('Percentage VWC')}</Typography.Text>
              </div>
            </div>
          )}
          {infoType === 'deficit' && (
            <div className="recommendation-detail-card2-deficit">
              <Typography.Text>{t('Percent Available Water')}</Typography.Text>
            </div>
          )}

          {infoType === 'vwc' && (
            <div style={{ display: 'flex' }}>
              <Select defaultValue={10} value={days} onSelect={setDays}>
                <Select.Option value={10}>{`10 ${t('Days')}`}</Select.Option>
                <Select.Option value={20}>{`20 ${t('Days')}`}</Select.Option>
              </Select>
            </div>
          )}
        </div>
        {infoType === 'deficit' && (
          <div style={{ backgroundColor: '#aaa', height: '200px' }}>
            <PercentAvailableWaterGraph blockId={Number(recommendation.block)} />
          </div>
        )}
        {infoType === 'vwc' && showGraphButtons && !showMap && (
          <div className="recommendation-detail-card2-deficit">
            <Typography.Text>{t('Percentage VWC')}</Typography.Text>
          </div>
        )}
        {infoType === 'vwc' && (
          <div style={{ backgroundColor: '#aaa', height: '200px', marginTop: '10px' }}>
            {sensor.identifier?rootZoneVWCChart:null}
          </div>
        )}
      </div>
    );
  }

  function renderEtcGraphs() {
    return (
      <div className="recommendation-detail-card2-container">
        <div className="recommendation-detail-card2-top">
          <div className="recommendation-detail-card2-deficit">
            <Typography.Text>{t('Etc Deficit')}</Typography.Text>
          </div>
        </div>
        <div style={{ backgroundColor: '#aaa', height: '200px' }}>
          <EtcDeficitGraph
            irrigationApplied={recommendation.irrigationApplied}
            etcHistoric={recommendation.etcHistoric}
            etcForecast={recommendation.etcForecast}
            irrigationScheduled={recommendation.irrigationScheduled}
            units={recommendation.units}
          />
        </div>
      </div>
    );
  }

  function renderGraphs() {
    if (irrigationModel === 'soil_water_deficit') {
      return renderSoilGraphs();
    }
    if (irrigationModel === 'etc_deficit') {
      return renderEtcGraphs();
    }
    return null;
  }

  const ranchBlockName = `${ranchName[blockId]} > ${blockName[blockId]}`;

  function getRecommendationMessage(modelType) {
    let modelTypeText = null;
    if (modelType === 'etc_deficit') modelTypeText = 'ETc';
    if (modelType === 'soil_water_deficit') modelTypeText = 'Soil';
    return modelTypeText ? (
      <small>
        <em>
          {t(`This recommendation was generated using the ${modelTypeText} Deficit Model.`)}
        </em>
      </small>
    ) : null;
  }

  function getPriorityText() {
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.URGENT) {
      return t('Urgent');
    }
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.HIGH) {
      return t('More Urgent');
    }
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.MONITOR) {
      return t('Monitor Closely');
    }
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.REDUCE) {
      return t('Reduce Water');
    }
    return t('Stable');
  }

  function getPriorityLevel() {
    if (
      recommendation?.state === IRRIGATION_RECOMMENDATION_STATES.VERY_DRY
      || recommendation?.forecastState === IRRIGATION_RECOMMENDATION_STATES.VERY_DRY
    ) {
      return RECOMMENDATION_PRIORITY_LEVELS.URGENT;
    }
    if (
      recommendation?.state === IRRIGATION_RECOMMENDATION_STATES.DRY
      || recommendation?.forecastState === IRRIGATION_RECOMMENDATION_STATES.DRY
    ) {
      return RECOMMENDATION_PRIORITY_LEVELS.HIGH;
    }
    if (recommendation?.state === IRRIGATION_RECOMMENDATION_STATES.DRYING) {
      return RECOMMENDATION_PRIORITY_LEVELS.MONITOR;
    }
    if (recommendation?.state === IRRIGATION_RECOMMENDATION_STATES.OVER_IRRIGATED) {
      return RECOMMENDATION_PRIORITY_LEVELS.REDUCE;
    }
    return RECOMMENDATION_PRIORITY_LEVELS.STABLE;
  }

  function getPriorityTooltipText() {
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.URGENT) {
      return t('Urgent means this block needs immediate attention based on current or forecast soil moisture.');
    }
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.HIGH) {
      return t('More Urgent means water need is increasing and should be reviewed soon.');
    }
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.MONITOR) {
      return t('Monitor Closely means the block is trending drier and should be watched.');
    }
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.REDUCE) {
      return t('Reduce Water means the block may be receiving too much irrigation.');
    }
    return t('Stable means no immediate irrigation issue is detected.');
  }

  function getStaleReasonText() {
    const staleReasonText = {
      missing_bounds: t('Block boundary is missing.'),
      missing_irrigation_metadata: t('Irrigation setup details are incomplete.'),
      no_sensors: t('No soil sensors are configured for this block.'),
      sensors_offline: t('Soil sensors are offline.'),
      unsupported_model: t('The selected irrigation model is not supported.'),
    };
    return staleReasonText[staleReason] || t('Required recommendation inputs are unavailable.');
  }

  function renderSummaryMetric(label, value, className = '', tooltip = null) {
    return (
      <Tooltip title={tooltip}>
        <div className={`recommendation-summary-metric ${className}`}>
          <Typography.Text className="recommendation-summary-metric-label">
            {label}
          </Typography.Text>
          <Typography.Text className="recommendation-summary-metric-value">
            {value}
          </Typography.Text>
        </div>
      </Tooltip>
    );
  }

  function getDepthUnitLabel() {
    return formatIrrigationDepthUnit(depthUnit, t);
  }

  function getUrgencyReason() {
    return recommendation?.urgencyReason
      || recommendation?.recommendationPriorityReason
      || recommendation?.priorityReason
      || recommendation?.data?.urgencyReason
      || recommendation?.data?.recommendationPriorityReason
      || recommendation?.data?.priorityReason
      || null;
  }

  function renderRefreshMeta() {
    const updatedText = getUpdatedText();
    if (!updatedText) {
      return null;
    }
    return (
      <Typography.Text className="recommendation-refresh-meta">
        {updatedText}
      </Typography.Text>
    );
  }

  function renderPriorityTag() {
    return (
      <Tooltip title={getPriorityTooltipText()}>
        <Typography.Text className={`recommendation-overview-priority recommendation-overview-priority-${getPriorityLevel()}`}>
          {getPriorityText()}
        </Typography.Text>
      </Tooltip>
    );
  }

  function renderCropTag() {
    if (!cropName) {
      return null;
    }
    return (
      <Tooltip title={t('Crop configured for this block.')}>
        <Typography.Text className="recommendation-overview-crop">
          {formatCropName(cropName)}
        </Typography.Text>
      </Tooltip>
    );
  }

  function renderStaleTag() {
    if (!isStale) {
      return null;
    }
    return (
      <Tooltip title={t('This recommendation is outdated and should not be acted on without checking details.')}>
        <Typography.Text className="recommendation-overview-stale">
          {t('Outdated')}
        </Typography.Text>
      </Tooltip>
    );
  }

  function getUpdatedText() {
    if (!recommendation?.dateCreated) return null;
    return t('Generated on {{time}}', {
      time: moment(recommendation.dateCreated).format('D MMM YYYY, h:mm a'),
    });
  }

  function getWindowLabelText() {
    return t('Best Window');
  }

  function isIrrigationAction() {
    return recommendation?.action === 'irrigate';
  }

  function getNoIrrigationTimingText() {
    if (recommendation?.action === 'stop_irrigation') {
      return t('Stop irrigation');
    }
    return t('No irrigation needed');
  }

  function getNoIrrigationWindowText() {
    if (recommendation?.action === 'stop_irrigation') {
      return t('Stop irrigation');
    }
    return t('No start needed');
  }

  function isDueDateNowOrPast() {
    return dueDate && moment(dueDate).isSameOrBefore(moment());
  }

  function getBestWindowValue() {
    return (
      <div className="recommendation-window-value">
        {!isIrrigationAction() ? (
          <strong className="recommendation-window-status-value">
            {getNoIrrigationWindowText()}
          </strong>
        ) : dueDate ? (
          <div className="recommendation-window-before">
            <span className="recommendation-window-before-label">
              {isDueDateNowOrPast() ? t('Recommended') : t('Before')}
            </span>
            <strong className="recommendation-window-before-value">
              {isDueDateNowOrPast()
                ? t('Today, now')
                : moment(dueDate).format('D MMM, h:mm a')}
            </strong>
          </div>
        ) : (
          <strong className="recommendation-window-status-value">
            {t('Not available')}
          </strong>
        )}
      </div>
    );
  }

  function getIrrigationDurationMetricValue() {
    if (!isIrrigationAction()) {
      return getNoIrrigationTimingText();
    }
    return `${formatRecommendationDuration(irrigationDurationValue)} ${t('hours')}`;
  }

  function formatRefreshCooldown() {
    const minutes = Math.floor(refreshCooldownSeconds / 60);
    const seconds = String(refreshCooldownSeconds % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  function getRefreshButtonText() {
    if (refreshCooldownSeconds > 0) {
      return t('Refresh available in {{time}}', {
        time: formatRefreshCooldown(),
      });
    }
    return t('Refresh');
  }

  async function onRefreshClick() {
    if (isRefreshing || refreshCooldownSeconds > 0) {
      return;
    }
    setIsRefreshing(true);
    setRefreshCooldownSeconds(REFRESH_COOLDOWN_SECONDS);
    try {
      await recommendationApi.refreshIrrigationRecommendation({ blockId });
      await dispatch(loadRecommendationForBlock(blockId));
    } finally {
      setIsRefreshing(false);
    }
  }

  function onPrimaryActionClick() {
    history.push({
      pathname: `/recommendation/action/${blockId}`,
      state: {
        from: location.pathname,
        selectedTab,
      },
    });
    tracking.track('Clicked recommendation action', recommendation);
  }

  function onUndoDismissClick() {
    dispatch(updateRecommendation({
      id: recommendation.id,
      type: 'irrigation',
      data: {
        isFrozen: false,
        dateFrozen: moment().toISOString(),
      },
    }));
    history.push({
      pathname: '/recommendation',
      state: { selectedTab },
    });
    tracking.track('Clicked Undo Dismissed recommendation', recommendation);
  }

  function onDismissClick() {
    dispatch(updateRecommendation({
      id: recommendation.id,
      type: 'irrigation',
      data: {
        isFrozen: true,
        dateFrozen: moment().toISOString(),
        freezeType: 'dismiss',
      },
    }));
    history.push({
      pathname: '/recommendation',
      state: { selectedTab },
    });
    tracking.track('Dismissed recommendation', recommendation);
  }

  function toggleCalculationDetails() {
    setTableVisibile((previousState) => !previousState);
  }

  function getActionButtonText() {
    if (isStale) {
      return t('Outdated recommendation');
    }
    if (recommendation?.action === 'no_action') {
      return t('Irrigate');
    }
    if (checkIsActionNeeded(recommendation)) {
      return t(getActionText(recommendation.action));
    }
    return t('Action Taken');
  }

  const fixedButtonPosition = !isMobile && tableVisible && 'recommendation-irrigation-action-buttons';

  function renderRecommendationActionButtons() {
    return (
      <div className={`${fixedButtonPosition} action-footer recommendation-detail-actions recommendation-buttons padding-bottom-10`}>
        {recommendation.action !== 'no_action' ? (
          <Button
            size="large"
            block
            type="primary"
            onClick={onPrimaryActionClick}
            disabled={isStale || !checkIsActionNeeded(recommendation)}
          >
            {getActionButtonText()}
          </Button>
        ) : (
          <Tooltip title={t('No irrigation action is needed for this recommendation right now.')}>
            <span className="recommendation-disabled-action-wrapper">
              <Button
                size="large"
                block
                type="primary"
                disabled
              >
                {getActionButtonText()}
              </Button>
            </span>
          </Tooltip>
        )}
        {recommendation.freezeType !== 'action' && (
        <>
          {recommendation.isFrozen && (
          <Button
            size="large"
            block
            onClick={onUndoDismissClick}
          >
            {t('Undo Dismiss')}
          </Button>
          )}
          {!recommendation.isFrozen && (
          <Button
            size="large"
            block
            onClick={onDismissClick}
          >
            {t('Dismiss')}
          </Button>
          )}
        </>
        )}
      </div>
    );
  }

  return (
    <>
      {!showMap ? (
        <div
          className="page-content page-container margin-10 recommendation-container"
          id="irrigation-recommendation-detail"
        >
          <PageHeader
            showBack
            toPath="/recommendation"
            title={<RanchBlockTitle title={ranchBlockName} />}
            state={{ selectedTab }}
          />

          <CardLayout key="1">
            <div className="recommendation-detail-card1-container">

              <div className="recommendation-overview-header">
                <div className="recommendation-overview-meta">
                  {renderCropTag()}
                  {renderPriorityTag()}
                  {renderStaleTag()}
                </div>
                <div className="recommendation-overview-state">
                  {renderRefreshMeta()}
                  <Button
                    type="text"
                    icon={<ReloadOutlined spin={isRefreshing} />}
                    onClick={onRefreshClick}
                    className="recommendation-refresh-button"
                    disabled={isRefreshing || refreshCooldownSeconds > 0}
                  >
                    {getRefreshButtonText()}
                  </Button>
                </div>
              </div>

              <RecommendationRefreshStatusMessage
                refreshStatus={block?.recommendationRefresh}
              />

              {isStale ? (
                <div className="recommendation-stale-detail-warning">
                  <Typography.Text className="recommendation-stale-detail-title">
                    {t('This recommendation is outdated. Do not use it to irrigate.')}
                  </Typography.Text>
                  <Typography.Text className="recommendation-stale-detail-reason">
                    {getStaleReasonText()}
                  </Typography.Text>
                </div>
              ) : null}

              <div className="recommendation-forecast-panel">
                <ForecastCard
                  recommendation={recommendation}
                />
              </div>

              <div className="recommendation-detail-copy">
                <Typography.Text className="recommendation-overview-subtitle">
                  {getIrrigationDescriptionLong()}
                </Typography.Text>
                {getUrgencyReason() ? (
                  <Typography.Text className="recommendation-urgency-reason">
                    {getUrgencyReason()}
                  </Typography.Text>
                ) : null}
              </div>

              <Typography.Text className="recommendation-forecast-label">
                {t('Forecasted values for the next 5 days')}
              </Typography.Text>

              <div className="recommendation-summary-grid">
                {renderSummaryMetric(
                  t('Current Deficit'),
                  deficitValue !== null ? `${formatIrrigationDepthValue(deficitValue)} ${getDepthUnitLabel()}` : t('Not available'),
                  '',
                  t('How far current soil moisture is from the refill target. Negative means surplus water.'),
                )}

                {renderSummaryMetric(
                  t('Forecast ETc Impact'),
                  etcImpactValue !== null ? `+${formatIrrigationDepthValue(etcImpactValue)} ${getDepthUnitLabel()}` : t('Not available'),
                  '',
                  t('Estimated crop water use from the weather forecast over the next 5 days.'),
                )}

                {renderSummaryMetric(
                  t('Water Needed'),
                  totalWaterValue !== null ? `${formatIrrigationDepthValue(totalWaterValue)} ${getDepthUnitLabel()}` : t('Not available'),
                  '',
                  t('Total water expected to be needed over the recommendation window.'),
                )}

                {renderSummaryMetric(
                  t('Irrigation Duration'),
                  getIrrigationDurationMetricValue(),
                  'recommendation-summary-metric-duration',
                  t('Estimated irrigation runtime needed for this recommendation.'),
                )}

                {renderSummaryMetric(
                  getWindowLabelText(),
                  getBestWindowValue(),
                  'recommendation-summary-metric-window',
                  t('Latest recommended time window before conditions become too dry.'),
                )}
              </div>

              <div className="recommendation-generated-meta">
                {getRecommendationMessage(irrigationModel)}
              </div>

            </div>
          </CardLayout>

          <CardLayout key="2">
            <RecommendationGraphs
              recommendation={recommendation}
              sensors={sensors}
            />
          </CardLayout>

          {/* Moved toggle OUTSIDE of CardLayout key="2" for cleaner visual separation */}
          <div className="recommendation-calculation-toggle" style={{ padding: '0 10px' }}>
            <Typography.Link onClick={toggleCalculationDetails}>
              {tableVisible ? t('Hide info...') : t('More info...')}
            </Typography.Link>
          </div>

          {tableVisible && (
            <CardLayout key="3" style={{ marginBottom: !isMobile && '45px'}}>
              <IrrigationEquationDetails
                recommendation={recommendation}
                units={units}
                ranchId={block?.ranch}
                customKcValue={customKcValue}
              />
            </CardLayout>
          )}
          {renderRecommendationActionButtons()}
          {!recommendation.isFrozen && recommendation.freezeType !== 'action' && (
            <Typography.Text className="recommendation-dismiss-note">
              {t('Dismiss will pause this irrigation recommendation until conditions change significantly.')}
            </Typography.Text>
          )}
        </div>
      ) : renderGraphs()}
    </>
  );
}

RecommendationDetail.propTypes = {
  showMap: PropTypes.bool,
  onClick: PropTypes.func,
  propBlockId: PropTypes.number,
  showGraphButtons: PropTypes.bool,
};

RecommendationDetail.defaultProps = {
  showMap: false,
  onClick: null,
  propBlockId: null,
  showGraphButtons: true,
};
